import React from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { ProfilePicture } from '@getro/rombo';
import PropTypes from 'prop-types';
import { ChevronDown, Copy } from 'lucide-react';
import copy from 'copy-to-clipboard';
import toast from 'react-hot-toast';
import { TableRow } from '../agGrid/components/tableRow';

export const ContactOfRenderer = ({ data, ...props }) => {
  if (!data?.original) {
    return '';
  }

  const names = data.contactOf.map((importer) => importer.name).join(', ');
  const visibleImporters = data.contactOf.slice(0, 3);
  const importerCount = Math.max(0, data.contactOf.length - visibleImporters.length);
  const rowOptions = props.api.getGridOption('context')[data.rowId] ?? {};

  const toggleContacts = () => {
    const context = props.api.getGridOption('context');
    const rowContext = context[data.rowId] ?? {};

    rowContext.showAll = !rowContext?.showAll;
    rowContext.height = rowContext?.showAll ? data.contactOf.length * 30 + 16 : data?.getRowHeight?.() ?? 40;

    props.node.setRowHeight(rowContext.height);

    props.api.setGridOption('context', {
      ...props.api.getGridOption('context'),
      [data.rowId]: rowContext,
    });

    props.api.onRowHeightChanged();
    props.api.redrawRows({
      rowNodes: [props.node],
    });
  };

  if (data.original.id === 'load-more') {
    return '';
  }

  if (data.contactOf.length === 0 && !data.original.contacts?.length) {
    return '';
  }

  if (data.contactOf.length === 0 && data.original.contacts?.length) {
    return (
      <Text padding="8px" fontSize="14px" fontStyle="italic" color="text.subtle">
        None
      </Text>
    );
  }

  return (
    <TableRow {...props}>
      <Flex
        sx={{ padding: '8px', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', width: '100%' }}
      >
        {!rowOptions?.showAll && (
          <Flex width="100%" alignItems="center" sx={{ gap: '8px' }}>
            <Flex sx={{ flexShrink: 0 }}>
              {visibleImporters.map((importer, index) => (
                <Box
                  key={`importer-avatar-${importer.id || importer.name}`}
                  width="24px"
                  height="24px"
                  p="2px"
                  ml={index === 0 ? '0' : '-10px'}
                  flex="0 0 auto"
                  sx={{ bg: 'neutral.0', borderRadius: 'circle', zIndex: 1 }}
                >
                  <ProfilePicture name={importer.name} imageUrl={importer.avatarUrl} />
                </Box>
              ))}
              {importerCount > 0 && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  bg="neutral.100"
                  width="24px"
                  height="24px"
                  ml="-14px"
                  flex="0 0 auto"
                  sx={{
                    borderRadius: 'circle',
                    zIndex: 1,
                    borderWidth: '2px',
                    borderColor: 'neutral.0',
                    borderStyle: 'solid',
                  }}
                >
                  <Text fontSize="0" fontWeight="medium">
                    {`+${importerCount}`}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Box>
              <Text
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '14px',
                  color: 'text.main',
                }}
              >
                {names}
              </Text>
            </Box>
          </Flex>
        )}
        {rowOptions?.showAll && (
          <Flex flexDirection="column" sx={{ gap: '4px' }}>
            {data.contactOf.map((importer) => (
              <Box display="grid" height="100%" width="100%" key={`importer-${importer.id}`}>
                <Flex key={`importer-${importer.id}`} flexDirection="column" width="100%" height="100%">
                  <Flex
                    alignItems="flex-start"
                    width="100%"
                    sx={{
                      gap: '8px',
                      '&:hover .table-item-importer-actions': {
                        visibility: 'visible',
                      },
                    }}
                  >
                    <Flex alignItems="center" flexGrow={1} sx={{ gap: '8px' }}>
                      <Box width="24px" height="24px" flex="0 0 auto">
                        <ProfilePicture imageUrl={importer.avatarUrl} name={importer.name} />
                      </Box>
                      <Text fontSize="14px">{importer.name}</Text>
                    </Flex>
                    {importer.email && (
                      <Flex
                        className="table-item-importer-actions"
                        alignItems="center"
                        flex="0 0 auto"
                        pt="4px"
                        sx={{ gap: '4px', visibility: 'hidden', color: 'neutral.500' }}
                      >
                        <Box
                          as={Copy}
                          height="16px"
                          width="16px"
                          aria-hidden="true"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            copy(importer.email);
                            toast('Importer Email copied', { icon: 'success', id: 'copy-importer-email' });
                          }}
                        />
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              </Box>
            ))}
          </Flex>
        )}
        {data.contactOf.length > 1 && (
          <Flex
            sx={{
              cursor: 'pointer',
              transform: rowOptions?.showAll ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out',
              color: 'neutral.500',
              height: '16px',
              width: '16px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={() => toggleContacts()}
          >
            <ChevronDown size={16} />
          </Flex>
        )}
      </Flex>
    </TableRow>
  );
};

ContactOfRenderer.propTypes = {
  data: PropTypes.object,
  context: PropTypes.object,
  api: PropTypes.object,
  node: PropTypes.object,
};

ContactOfRenderer.defaultProps = {
  data: {},
  context: {},
  api: {},
  node: {},
};
